import { Differ } from 'json-diff-kit';
import type { DiffResult } from 'json-diff-kit';
import Papa from 'papaparse';
import { Dispatch, SetStateAction } from 'react';

import {
  ENV_CLOUDFRONT_APP_DOMAIN,
  REQUEST_METHODS,
  isApplicationKiosk,
  isApplicationPmi,
} from '@lib/core/service/consts';
import { request } from '@lib/core/service/requests/request';
import { selectIsLocaleEditMode, selectLocale } from '@lib/core/service/selectors';
import { store } from '@lib/core/service/store';
import { isLocalhost } from '@lib/core/service/utils';
import { ILocaleTableDataRow } from '@lib/tools/devtools/components/interfaces';
import localePmiEnglishTerms from '@lib/tools/locale/dist/pmi/en.json';
import localeWebEnglishTerms from '@lib/tools/locale/dist/web/en.json';
import { currencies, languages } from '@lib/tools/locale/utils/consts';

/**
 * * backend and i18n utils
 * ! Add logger
 * ! Improve auth
 * ! Improve error handling
 * 0. Import/export terms in CSV format
 * 1. Read/write from backend ONLY in edit mode. End users cannot hit backend.
 * 2. Publish terms as JSON files to S3 when ready, and invalidate CloudFront cache.
 *
 */
export class LocaleUtils {
  private static appTypeEnv = isApplicationPmi ? 'fla' : 'vin';

  private static vinhoodEnv = isApplicationKiosk ? 'kiosk' : 'app';

  private static localeEnv = isApplicationPmi ? 'fla' : this.vinhoodEnv;

  /**
   * Returns the API absolute URL to retrieve the localization file from the backend.
   */
  public static localeDataApiUrlCreator = ({ locale }) => {
    return `/localization/${this.appTypeEnv}/${this.localeEnv}/${locale}/`;
  };

  /**
   * Returns the absolute URL to retrieve the localization file from the S3 bucket (CloudFront).
   */
  public static localeDataS3UrlCreator = ({ locale }: { locale: string }) => {
    return `https://${ENV_CLOUDFRONT_APP_DOMAIN}/localization/${locale}.json`;
  };

  private static draftTerms = {};

  public static publishedTerms = {};

  public static getCurrencySymbolFromCode(currency) {
    return currencies[currency] || '';
  }

  /**
   * ! Only for DevTools LocalePanel
   * ! Add solid validations
   * ! Display diff between backend locale and S3 file
   * ! Save publish logs on backend
   */
  public static publishTerms = async () => {
    const locale = selectLocale(store.getState());

    if (locale === languages.ENGLISH) return;

    try {
      const response = await request({
        body: {
          isPublished: true,
        },
        isDevtoolsRequest: true,
        method: REQUEST_METHODS.PATCH,
        url: this.localeDataApiUrlCreator({ locale }),
      });

      if (!response) {
        throw new Error(`Failed to update terms for locale ${locale}`);
      }
    } catch (e) {
      // ! Sentry
      console.error(e);
    }
  };

  /**
   * Retrieves the published translation terms for a given locale.
   *
   * - In development (localhost) and for English ('en'), returns the local static English terms.
   * - In development (localhost) or dynamic environments (e.g., .web.vinhood.dev) and for non-English locales,
   *   fetches the terms from the CloudFront S3 bucket.
   * - In all other cases (typically production), fetches the terms from the default /locale/{locale}.json endpoint.
   * - If fetching fails, falls back to the English terms.
   *
   * This method should be called only once during the app lifecycle to initialize published terms.
   *
   * @param locale - The locale code (e.g., 'en', 'it', 'fr') for which to retrieve published terms.
   * @returns The published terms object for the specified locale.
   */
  public static getPublishedTerms = async (locale: string) => {
    try {
      if (locale !== 'en') {
        const publishedTerms = await fetch(this.localeDataS3UrlCreator({ locale }));
        this.publishedTerms = await publishedTerms.json();
        return this.publishedTerms;
      }

      if (isLocalhost() && locale === 'en') {
        this.publishedTerms = isApplicationPmi ? localePmiEnglishTerms : localeWebEnglishTerms;
        return this.publishedTerms;
      }

      const publishedTerms = await fetch(`/locale/en.json`);
      this.publishedTerms = await publishedTerms.json();
      return this.publishedTerms;
    } catch (e) {
      console.error(`Error loading published terms for locale ${locale}:`, e);

      const fallbackTerms = await fetch(`/locale/en.json`);
      return await fallbackTerms.json();
    }
  };

  /**
   * ! Show only useful diff. Needs additional parsing
   */
  public static generateDiff = (publishedTerms, draftTerms): readonly [DiffResult[], DiffResult[]] => {
    const publishedCopy = { ...publishedTerms };
    const draftCopy = { ...draftTerms };

    Object.keys(publishedCopy).forEach(context => {
      if (publishedCopy[context] === draftCopy[context] || !draftCopy[context]) {
        delete publishedCopy[context];
        delete draftCopy[context];
      }
    });

    const differ = new Differ({
      arrayDiffMethod: 'lcs',
      detectCircular: true,
      maxDepth: 4,
      showModifications: true,
    });

    return differ.diff(publishedCopy, draftCopy);
  };

  /**
   * ! Only for DevTools LocalePanel
   * * CSV export of terms from S3 for a specific locale
   * * Headers: term_id, en, <locale>
   */
  public static exportTerms = async (tableData: ILocaleTableDataRow[]) => {
    const locale = selectLocale(store.getState());
    const localTermsEn = await this.getPublishedTerms(languages.ENGLISH);
    const downloadCsv = data => {
      const csvRows = [];
      const headers = ['term_id', languages.ENGLISH, locale];
      csvRows.push(headers.join(','));

      data.forEach(termEntry => {
        const termEn = localTermsEn[termEntry.context] || '';
        const termContext = termEntry.context || '';
        const termDraftText = termEntry.draftText || '';
        const row = [termContext, termEn, termDraftText];
        const escapedRow = row.map(cell => `"${String(cell).replace(/"/g, '""')}"`);
        csvRows.push(escapedRow.join(','));
      });

      const csvData = csvRows.join('\n');
      const blob = new Blob([csvData], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${locale}.csv`;
      link.click();
    };

    downloadCsv(tableData);
  };

  /**
   * ! Replaces all terms with s3 terms
   */
  public static restorePublishedTermsNew = async () => {
    await Promise.all(
      Object.values(languages).map(async locale => {
        try {
          const fetchUrl =
            isLocalhost() && locale !== 'en' ? this.localeDataS3UrlCreator({ locale }) : `/locale/${locale}.json`;

          const publishedTerms = await fetch(fetchUrl);
          const terms = await publishedTerms.json();

          const response = await request({
            body: {
              translations: terms,
            },
            isDevtoolsRequest: true,
            method: REQUEST_METHODS.PUT,
            url: this.localeDataApiUrlCreator({ locale }),
          });

          if (!response) {
            throw new Error(`Failed to update terms for locale ${locale}`);
          }
        } catch (e) {
          console.error(e);
        }
      }),
    );
  };

  /**
   * * Obtain draft terms from backend
   */
  public static getDraftTerms = async (locale, callback: Dispatch<SetStateAction<object>>) => {
    const isLocaleEditMode = selectIsLocaleEditMode(store.getState());
    const response = await request({
      isDevtoolsRequest: true,
      url: this.localeDataApiUrlCreator({ locale }),
    });

    try {
      const terms = await response.translations;
      const isAvailable = Object.keys(terms).length;

      if (isLocaleEditMode && isAvailable) {
        this.draftTerms = terms;
        callback(this.draftTerms);
      } else {
        console.warn('Draft terms are not available or locale edit mode is off');
      }
    } catch (error) {
      console.error('Error fetching localization terms:', error);
    }
  };

  /**
   * * Update a translation using context
   */
  public static updateTerm = async (context, translation) => {
    const locale = selectLocale(store.getState());
    return await request({
      body: {
        isPublished: false,
        translations: { ...this.draftTerms, [context]: translation },
      },
      isDevtoolsRequest: true,
      method: REQUEST_METHODS.PATCH,
      url: this.localeDataApiUrlCreator({ locale }),
    });
  };

  /**
   * ! Only for DevTools LocalePanel
   * * CSV import of terms into backend for a specific locale
   * * Headers: term_id, en, <locale>
   * The `en` field is ignored and only used for reference.
   */
  public static importTerms = async () => {
    const locale = selectLocale(store.getState());

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';

    fileInput.addEventListener('change', async event => {
      // @ts-ignore
      const file = event.target.files[0];

      if (file) {
        const reader = new FileReader();
        reader.onload = async e => {
          const csvData = e.target?.result;
          const result = Papa.parse(csvData, { header: true });
          const jsonObjects = result.data;
          const importedTerms = {};

          jsonObjects.forEach(row => {
            importedTerms[row.term_id] = row[locale];
          });

          try {
            const response = await request({
              body: { translations: importedTerms },
              isDevtoolsRequest: true,
              method: REQUEST_METHODS.PUT,
              url: this.localeDataApiUrlCreator({ locale }),
            });

            if (!response.ok) {
              throw new Error(`Failed to import terms: ${response.statusText}`);
            }
          } catch (error) {
            console.error('Error importing terms:', error);
          }
        };

        reader.readAsText(file);
      }
    });

    fileInput.click();
  };
}
